<script setup>
import { storeToRefs } from 'pinia'

import { HelpTextTooltip, TextField } from '@haskoning/vue-component-library'

import { useCalculationStore } from '@/stores/calculation'
import { useOptimisationStore } from '@/stores/optimisation'

import { CONTINUOUS } from '@/components/calculation/calculation.constant'
import RHDHVButton from '@/components/common/RHDHVButton.vue'
import {
  BERTH_TYPE,
  MAXIMUM_NUMBER_OF_LINES,
  MIN_LINE_LENGTH,
  MOORING_POINT_SPACING,
  MOORING_POINT_Y,
  PLATFORM_LENGTH,
  PLATFORM_WIDTH,
  QUAY_LEVEL,
  START_MOORING_POINT_X,
  Y_LOCATION_PLATFORM
} from '@/constants/calculationFieldConstants'

const calculationStore = useCalculationStore()
const optimisationStore = useOptimisationStore()

const { currentItem, fields } = storeToRefs(calculationStore)
const { generateLoading } = storeToRefs(optimisationStore)

function customValidatorMooringPointY(value) {
  return currentItem.value[BERTH_TYPE] === CONTINUOUS &&
    value > currentItem.value[Y_LOCATION_PLATFORM]
    ? 'Mooring point Y-coordinate must be smaller than platform Y-coordinate'
    : true
}
</script>

<template>
  <v-list-item class="pb-0 pt-2">
    <v-list-item-title>
      <span> Mooring point configuration </span>
      <RHDHVButton
        class="float-right"
        :loading="generateLoading"
        item-class="ml-0"
        allowed="add_mooringpoint"
        auth-source="ProjectUser"
        variant="elevated"
        size="small"
        elevation="0"
        prepend-icon="mdi-plus-box-multiple"
        @click="optimisationStore.generate()"
      >
        Generate
      </RHDHVButton>
    </v-list-item-title>

    <v-row class="mt-2 mb-0">
      <v-col cols="6" class="py-0">
        <TextField
          :item-key="MAXIMUM_NUMBER_OF_LINES"
          :field="fields[MAXIMUM_NUMBER_OF_LINES]"
          :model-value="currentItem"
          @update="calculationStore.update()"
        ></TextField>
      </v-col>
      <v-col cols="6" class="py-0">
        <TextField
          :item-key="MIN_LINE_LENGTH"
          :field="fields[MIN_LINE_LENGTH]"
          :model-value="currentItem"
          @update="calculationStore.update()"
        ></TextField>
      </v-col>
      <v-col cols="6" class="py-0">
        <TextField
          :item-key="START_MOORING_POINT_X"
          :field="fields[START_MOORING_POINT_X]"
          :model-value="currentItem"
          @update="calculationStore.update()"
        ></TextField>
      </v-col>
      <v-col cols="6" class="py-0">
        <TextField
          :item-key="MOORING_POINT_SPACING"
          :field="fields[MOORING_POINT_SPACING]"
          :model-value="currentItem"
          warning
          :warning-rules="[
            {
              min_value: 15,
              message: 'This is a relatively low spacing'
            },
            {
              max_value: 30,
              message: 'This is a relatively high spacing'
            }
          ]"
          @update="calculationStore.update()"
        ></TextField>
      </v-col>
      <v-col cols="6" class="py-0">
        <TextField
          :custom-error-rules="[customValidatorMooringPointY]"
          custom-label="Mooring point Y-coordinate [m]"
          :item-key="MOORING_POINT_Y"
          :field="fields[MOORING_POINT_Y]"
          :model-value="currentItem"
          @update="calculationStore.update()"
        ></TextField>
      </v-col>
    </v-row>
  </v-list-item>
  <v-divider></v-divider>
  <v-list-item>
    <v-list-item-title class="pr-3 py-2">
      <span class="mr-3"> Berth platform size and location</span>
      <HelpTextTooltip
        :text="'Define the size and location of the berth (Y-coordinate is lateral distance of quay/deck front behind the berthing line)'"
        size="small"
      ></HelpTextTooltip>
    </v-list-item-title>
    <v-row class="my-0">
      <v-col cols="6" class="py-0">
        <TextField
          v-if="fields[QUAY_LEVEL]"
          :item-key="QUAY_LEVEL"
          :field="fields[QUAY_LEVEL]"
          :model-value="currentItem"
          @update="calculationStore.update()"
        ></TextField>
      </v-col>
      <v-col cols="6" class="py-0">
        <TextField
          v-if="fields[Y_LOCATION_PLATFORM]"
          :item-key="Y_LOCATION_PLATFORM"
          :field="fields[Y_LOCATION_PLATFORM]"
          :model-value="currentItem"
          :step="0.5"
          @update="calculationStore.update()"
        ></TextField>
      </v-col>
      <v-col cols="6" class="py-0">
        <TextField
          v-if="fields[PLATFORM_WIDTH]"
          style="z-index: 1"
          :item-key="PLATFORM_WIDTH"
          :field="fields[PLATFORM_WIDTH]"
          :model-value="currentItem"
          :step="0.5"
          warning
          :warning-rules="[
            {
              min_value: 10,
              message: 'This is a relatively small platform width.'
            }
          ]"
          @update="calculationStore.update()"
        ></TextField>
      </v-col>
      <v-col cols="6" class="py-0">
        <TextField
          v-if="fields[PLATFORM_LENGTH]"
          style="z-index: 1"
          :item-key="PLATFORM_LENGTH"
          :field="fields[PLATFORM_LENGTH]"
          :model-value="currentItem"
          :step="0.5"
          warning
          :warning-rules="[
            {
              min_value: 10,
              message: 'This is a relatively small platform width.'
            }
          ]"
          @update="calculationStore.update()"
        ></TextField>
      </v-col>
    </v-row>
  </v-list-item>
</template>
